type WorkspaceCardStatus = "view" | "editing" | "saving"

interface WorkspaceCardEditState<V extends object> {
  status: WorkspaceCardStatus
  draft: V | null
  error: string | null
}

type WorkspaceCardEditAction<V extends object> =
  | { type: "edit"; values: V }
  | { type: "change"; values: Partial<V> }
  | { type: "cancel" }
  | { type: "discard" }
  | { type: "save" }
  | { type: "saved" }
  | { type: "cancelled" }
  | { type: "failed"; error: string }

const workspaceCardInitialState = <
  V extends object
>(): WorkspaceCardEditState<V> => ({
  status: "view",
  draft: null,
  error: null
})

function workspaceCardEditReducer<V extends object>(
  state: WorkspaceCardEditState<V>,
  action: WorkspaceCardEditAction<V>
): WorkspaceCardEditState<V> {
  switch (action.type) {
    case "edit":
      if (state.status !== "view") return state
      return { status: "editing", draft: { ...action.values }, error: null }

    case "change":
      if (state.status !== "editing" || !state.draft) return state
      return { ...state, draft: { ...state.draft, ...action.values } }

    case "cancel":
      if (state.status !== "editing") return state
      return workspaceCardInitialState<V>()

    // Unlike Cancel, a discard also abandons a save already in flight; the
    // reducer ignores that request's outcome because it is no longer "saving".
    case "discard":
      return workspaceCardInitialState<V>()

    case "save":
      if (state.status !== "editing") return state
      return { ...state, status: "saving" }

    case "saved":
      if (state.status !== "saving") return state
      return workspaceCardInitialState<V>()

    case "cancelled":
      if (state.status !== "saving") return state
      return { status: "editing", draft: state.draft, error: null }

    case "failed":
      if (state.status !== "saving") return state
      return { status: "editing", draft: state.draft, error: action.error }

    default:
      return state
  }
}

export {
  type WorkspaceCardStatus,
  type WorkspaceCardEditState,
  type WorkspaceCardEditAction,
  workspaceCardInitialState,
  workspaceCardEditReducer
}
